import { useEffect } from 'react';
import { Spinner } from '../../../../../../assets/icon/svg';

export const TEXT_FILE = [
    'txt',
    'csv',
    'json',
    'xml',
    'md',
    'log',
    'yaml',
    'yml'
]

export const AUDI_FILE = [
    'mp3',
    'wav',
    'ogg',
    'm4a',
    'aac',
    'flac',
    'wma',
]

export const VIDEO_FILE = [
    'mp4',
    'webm',
    'mov',
    'avi',
    'mkv',
    'm4v',
]

const getFileExtension = (fileName = '') => fileName.split('.').pop()?.toLowerCase() ?? '';
const isPdfFile = (fileName = '') => getFileExtension(fileName) === 'pdf';
const isTextFile = (fileName = '') => TEXT_FILE.includes(getFileExtension(fileName));
const isAudioFile = (fileName = '') => AUDI_FILE.includes(getFileExtension(fileName));
const isVideoFile = (fileName = '') => VIDEO_FILE.includes(getFileExtension(fileName));

interface Props {
    isOpen: boolean;
    fileUrl: string | null;
    fileName?: string;
    fileType?: string;
    isLoading?: boolean;
    onClose: () => void;
}

export default function FileAttachmentPreviewModal({ isOpen, fileUrl, fileName, fileType, isLoading = false, onClose, }: Props) {
    
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const pdf = isPdfFile(fileName);
    const text = isTextFile(fileName);
    const audio = isAudioFile(fileName);
    const video = isVideoFile(fileName);

    return (
        <div onClick={onClose} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-3 backdrop-blur-sm sm:p-5">
            <div onClick={(event) => event.stopPropagation()} className="relative flex h-full max-h-[95vh] w-full max-w-[1200px] flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
                {/* Header */}
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-3 py-2.5 sm:px-4">
                    <div className="min-w-0">
                        <p title={fileName} className="truncate text-[11px] font-medium text-gray-800 sm:text-xs">
                            {fileName}
                        </p>
                        <p className="mt-0.5 text-[9px] uppercase text-gray-400">
                            {fileType || getFileExtension(fileName) || 'File'}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                        title="Close (Esc)"
                        aria-label="Close file preview"
                    >
                        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" >
                            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="min-h-0 flex-1 overflow-hidden bg-gray-100">
                    {isLoading ? (
                        <div className="flex h-full items-center justify-center">
                            <div className="flex flex-col items-center gap-2">
                                <Spinner size={22} />
                                <p className="text-[10px] text-gray-500">
                                    Loading file...
                                </p>
                            </div>
                        </div>
                    ) : fileUrl && pdf ? (
                        <iframe
                            src={fileUrl}
                            title={fileName ?? 'PDF preview'}
                            className="h-full w-full border-0"
                        />
                    ) : fileUrl && text ? (
                        <iframe
                            src={fileUrl}
                            title={fileName ?? 'Text file preview'}
                            className="h-full w-full border-0 bg-white"
                        />
                    ) : fileUrl && audio ? (
                        <div className="flex h-full items-center justify-center p-5">
                            <div className="w-full max-w-[500px] rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                                <div className="mb-4 flex items-center gap-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                                        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" >
                                            <path d="M9 18V5l10-2v13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                            <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.8" />
                                            <circle cx="16" cy="16" r="3" stroke="currentColor" strokeWidth="1.8" />
                                        </svg>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="truncate text-xs font-medium text-gray-800">
                                            {fileName}
                                        </p>
                                        <p className="text-[9px] text-gray-400">
                                            Audio preview
                                        </p>
                                    </div>
                                </div>
                                <audio src={fileUrl} controls autoPlay={false} className="w-full" />
                            </div>
                        </div>
                    ) : fileUrl && video ? (
                        <div className="flex h-full items-center justify-center bg-black p-3 sm:p-5">
                            <video
                                src={fileUrl}
                                controls
                                className="max-h-full max-w-full object-contain"
                            />
                        </div>
                    ) : (
                        <div className="flex h-full items-center justify-center p-5">
                            <div className="text-center">
                                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gray-200 text-gray-500">
                                    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" >
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                        <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <p className="text-xs font-medium text-gray-700">
                                    Preview not available
                                </p>
                                <p className="mt-1 text-[10px] text-gray-400">
                                    This file type cannot be
                                    displayed in the browser.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}